import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

const DRAG_THRESHOLD_PX = 5;

export type UseDragToScrollOptions = {
  /** Re-run scrollability detection when content changes (e.g. children.length). */
  contentVersion?: unknown;
  /**
   * Override scrollability for drag. When omitted, uses DOM measurement (scrollWidth > clientWidth).
   */
  enabled?: boolean;
  /** Called during drag and on drag end (e.g. to sync scroll button visibility). */
  onScroll?: () => void;
};

export default function useDragToScroll(
  scrollRef: RefObject<HTMLDivElement | null>,
  options?: UseDragToScrollOptions
) {
  const { contentVersion, enabled: enabledOverride, onScroll } = options ?? {};

  const cleanupDrag = useRef<(() => void) | null>(null);
  const clickResetTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressNextClick = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const [domScrollable, setDomScrollable] = useState(false);
  const isScrollable = enabledOverride ?? domScrollable;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    const updateScrollable = () => {
      setDomScrollable(el.scrollWidth > el.clientWidth);
    };

    updateScrollable();

    const resizeObserver = new ResizeObserver(updateScrollable);
    resizeObserver.observe(el);
    el.addEventListener('scroll', updateScrollable);

    return () => {
      resizeObserver.disconnect();
      el.removeEventListener('scroll', updateScrollable);
    };
  }, [scrollRef, contentVersion]);

  useEffect(() => {
    if (!isScrollable) cleanupDrag.current?.();
    return () => {
      cleanupDrag.current?.();
      if (clickResetTimeout.current !== null) clearTimeout(clickResetTimeout.current);
      suppressNextClick.current = false;
    };
  }, [isScrollable]);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const el = scrollRef.current;
      if (event.button !== 0 || !isScrollable || !el) return;

      cleanupDrag.current?.();
      if (clickResetTimeout.current !== null) clearTimeout(clickResetTimeout.current);
      suppressNextClick.current = false;

      const startX = event.clientX;
      const startScrollLeft = el.scrollLeft;
      let dragged = false;
      let animationFrame: number | null = null;
      let previousCursor: string;
      let previousUserSelect: string;

      const handleMove = (moveEvent: MouseEvent) => {
        const totalDx = startX - moveEvent.clientX;
        // Ordinary clicks must not change styles, scroll, or rerender the carousel.
        if (!dragged && Math.abs(totalDx) <= DRAG_THRESHOLD_PX) return;

        if (!dragged) {
          dragged = true;
          previousCursor = document.body.style.cursor;
          previousUserSelect = document.body.style.userSelect;
          document.body.style.cursor = 'grabbing';
          document.body.style.userSelect = 'none';
          setIsDragging(true);
        }

        el.scrollLeft = startScrollLeft + totalDx;
        if (onScroll && animationFrame === null) {
          animationFrame = window.requestAnimationFrame(() => {
            animationFrame = null;
            onScroll();
          });
        }
      };

      const handleUp = () => {
        cleanupDrag.current?.();
        if (!dragged) return;

        suppressNextClick.current = true;
        onScroll?.();
        clickResetTimeout.current = setTimeout(() => {
          suppressNextClick.current = false;
          clickResetTimeout.current = null;
        }, 100);
      };

      const cleanup = () => {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleUp);
        window.removeEventListener('blur', cleanup);
        if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
        if (dragged) {
          document.body.style.cursor = previousCursor;
          document.body.style.userSelect = previousUserSelect;
          setIsDragging(false);
        }
        cleanupDrag.current = null;
      };

      cleanupDrag.current = cleanup;
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleUp);
      window.addEventListener('blur', cleanup);
    },
    [isScrollable, onScroll, scrollRef]
  );

  const handleClickCapture = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (suppressNextClick.current) {
      event.preventDefault();
      event.stopPropagation();
      suppressNextClick.current = false;
    }
  }, []);

  return {
    isDragging,
    isScrollable,
    handleMouseDown,
    handleClickCapture,
  };
}
